/**
 * B524 — the GA4 recommended-ecommerce events the web shells push for a
 * tenant's OWN Google Tag Manager container (Pathik 2026-09-23: "we also
 * need to add gtm support with standard ecommerce events").
 *
 * ONE definition of the wire a tenant's tags read: the closed event set,
 * the `items[]` shape, the `transact` context block. Every push — relayed
 * over the embed protocol to the host page's `dataLayer`, or pushed onto
 * the hosted storefront's own — is parsed by `zGtmEvent` first, so a
 * payload that is not exactly this shape never leaves the page.
 *
 * NO PII, by construction rather than by care: every object is `.strict()`
 * (an unknown key is a refusal, not a pass-through), and the wall in
 * `test/gtm.test.ts` walks this schema's keys and refuses any that names a
 * person (email, name, phone, address, …). `item_name` is the PRODUCT's
 * name and `affiliation` the BUSINESS's — both allow-listed by name.
 *
 * Lives in contracts, not domain: domain stays zod-free and I/O-free, and
 * this is a wire shape (what crosses to a third party's tags), which is
 * what this package is for.
 */
import { z } from "zod";

/** The closed GA4 ecommerce vocabulary (exact names — GTM triggers match on them). */
export const GTM_EVENTS = [
  "view_item_list",
  "select_item",
  "view_item",
  "add_to_cart",
  "remove_from_cart",
  "view_cart",
  "begin_checkout",
  "add_payment_info",
  "purchase",
  "refund",
] as const;
export type GtmEventName = (typeof GTM_EVENTS)[number];

/** How the buyer chose to pay (`add_payment_info.payment_type`). */
export const GTM_PAYMENT_TYPES = ["apple_pay", "google_pay", "card"] as const;
export type GtmPaymentType = (typeof GTM_PAYMENT_TYPES)[number];

/** GTM's own container id shape (`GTM-` + 4..12 upper-case alphanumerics). */
export const GTM_CONTAINER_ID = /^GTM-[A-Z0-9]{4,12}$/;
export function isGtmContainerId(value: unknown): value is string {
  return typeof value === "string" && GTM_CONTAINER_ID.test(value);
}

const zCurrency = z.string().regex(/^[A-Z]{3}$/);
/** GA4 wants a NUMBER in major units; the storefront derives it from integer minor units (money.ts), never float arithmetic. */
const zAmount = z.number().finite().nonnegative();
const zText = z.string().min(1).max(200);

export const zGtmItem = z
  .object({
    item_id: zText,
    item_name: zText,
    item_variant: zText.optional(),
    item_category: zText.optional(),
    item_list_id: zText.optional(),
    price: zAmount.optional(),
    quantity: z.number().int().positive().optional(),
    index: z.number().int().nonnegative().optional(),
    affiliation: zText.optional(),
    coupon: zText.optional(),
  })
  .strict();
export type GtmItem = z.infer<typeof zGtmItem>;

const zItems = z.array(zGtmItem).max(50);

/** The ecommerce payload, per event (GA4's recommended parameters). */
const zListPayload = z.object({ item_list_id: zText.optional(), items: zItems }).strict();
const zValuePayload = z.object({ currency: zCurrency, value: zAmount, items: zItems }).strict();
const zCheckoutPayload = z
  .object({ currency: zCurrency, value: zAmount, coupon: zText.optional(), items: zItems })
  .strict();
const zPaymentPayload = z
  .object({ currency: zCurrency, value: zAmount, payment_type: z.enum(GTM_PAYMENT_TYPES), items: zItems })
  .strict();
const zPurchasePayload = z
  .object({
    transaction_id: zText,
    currency: zCurrency,
    value: zAmount,
    tax: zAmount.optional(),
    coupon: zText.optional(),
    affiliation: zText.optional(),
    items: zItems,
  })
  .strict();
const zRefundPayload = z
  .object({ transaction_id: zText, currency: zCurrency, value: zAmount, items: zItems.optional() })
  .strict();

/** Where the event came from — for the tenant's own triggers. Ids only, never a person. */
export const zGtmContext = z
  .object({
    channelId: zText,
    distribution: z.enum(["hosted", "embedded"]),
    presentation: z.enum(["list", "dialog", "inline"]).optional(),
  })
  .strict();
export type GtmContext = z.infer<typeof zGtmContext>;

function event<N extends GtmEventName, P extends z.ZodTypeAny>(name: N, payload: P) {
  return z.object({ event: z.literal(name), ecommerce: payload, transact: zGtmContext }).strict();
}

export const zGtmEvent = z.discriminatedUnion("event", [
  event("view_item_list", zListPayload),
  event("select_item", zListPayload),
  event("view_item", zValuePayload),
  event("add_to_cart", zValuePayload),
  event("remove_from_cart", zValuePayload),
  event("view_cart", zValuePayload),
  event("begin_checkout", zCheckoutPayload),
  event("add_payment_info", zPaymentPayload),
  event("purchase", zPurchasePayload),
  event("refund", zRefundPayload),
]);
export type GtmEvent = z.infer<typeof zGtmEvent>;

/**
 * The two pushes GA4 asks for, in order: `{ ecommerce: null }` clears the
 * previous event's ecommerce object in GTM's data model (otherwise items
 * from `add_to_cart` bleed into the next `purchase`), then the event. Both
 * the embed loader (on the HOST page) and the hosted storefront push
 * through this one shape.
 */
export function dataLayerPushes(e: GtmEvent): readonly [{ readonly ecommerce: null }, GtmEvent] {
  return [{ ecommerce: null }, e];
}
